import { LiveBidConsoleCallbacks } from '@liveauctioneers/hammer-ui-liveauctioneers/desktopLiveBidPanel';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useHandleLinkClicked } from '@/components/ItemBidding/components/BidCanvasContainer/hooks/useHandleBidCanvasCallbacks/useHandleLinkClicked';
import { usePlaceBid } from '@/components/ItemBidding/components/BidCanvasContainer/hooks/useHandleBidCanvasCallbacks/usePlaceBid';
import { useRegisterForAuction } from '@/components/ItemBidding/components/BidCanvasContainer/hooks/useHandleBidCanvasCallbacks/useRegisterForAuction';
import { useSendSellerMessage } from '@/components/ItemBidding/components/BidCanvasContainer/hooks/useHandleBidCanvasCallbacks/useSendSellerMessage';
import { useAppDispatch } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import { CompleteAccountModalContext, openCompleteAccountSignUpModal, openLoginModal } from '@/redux/modules/modal';
import { getItemIdFromPath } from '@/utils/urls';

type Props = {
    catalogId: number;
    itemId: number;
};

export const useGetBiddingConsoleCallbacks = ({ catalogId, itemId }: Props): LiveBidConsoleCallbacks => {
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const itemIdFromPath = getItemIdFromPath(pathname);

    // Reuse existing callback hooks
    const onPlaceBidClickedBase = usePlaceBid({ itemId });
    const onRegisterForAuctionClicked = useRegisterForAuction({ catalogId, itemId });

    const onPlaceBidClicked = useCallback(() => {
        onPlaceBidClickedBase('Place Bid Button');
    }, [onPlaceBidClickedBase]);
    const onSendMessageClicked = useSendSellerMessage({ itemId });
    const onLinkClicked = useHandleLinkClicked({ itemId });

    const onLoginClicked = useCallback(() => {
        dispatch(
            openLoginModal({
                eventContext: EventContext.loginModal,
                id: itemIdFromPath,
                itemId: itemIdFromPath,
                type: CompleteAccountModalContext.Login,
            })
        );
    }, [dispatch, itemIdFromPath]);

    const onSignUpClicked = useCallback(() => {
        dispatch(
            openCompleteAccountSignUpModal({
                eventContext: EventContext.signUpModal,
                id: itemIdFromPath,
                itemId: itemIdFromPath,
                type: CompleteAccountModalContext.Join,
            })
        );
    }, [dispatch, itemIdFromPath]);

    const onTipsForGettingApprovedClicked = useCallback(() => {
        onLinkClicked('tips_for_getting_approved');
    }, [onLinkClicked]);

    return useMemo(
        () => ({
            onLoginClicked,
            onPlaceBidClicked,
            onRegisterForAuctionClicked,
            onSendMessageClicked,
            onSignUpClicked,
            onTipsForGettingApprovedClicked,
        }),
        [
            onLoginClicked,
            onPlaceBidClicked,
            onRegisterForAuctionClicked,
            onSendMessageClicked,
            onSignUpClicked,
            onTipsForGettingApprovedClicked,
        ]
    );
};
